import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Plus, Printer, Search } from 'lucide-react';
import Button from '../ui/Button';
import FormModal, { Field, inputClass } from '../fieldWork/FormModal';
import harvestService from '../../services/harvest';
import fieldWorkService from '../../services/fieldWork';
import orchardService from '../../services/orchard';
import { errorMessage } from '../../utils/apiResponse';
import { getTodayInTz, useOrgTimezone } from '../../utils/timezone';
import { workerDisplayName, type Worker } from '../../types/fieldWork';
import { binPlace, LABEL_SIZE_LABELS, type Bin, type HarvestSettings, type LabelSize } from '../../types/harvest';
import { usePlaceOptions } from '../labour/labourUtils';
import { Notice } from '../labour/shared';

const FILLS: { value: number; label: string }[] = [
  { value: 1, label: 'Full' }, { value: 0.75, label: '¾' }, { value: 0.5, label: '½' }, { value: 0.25, label: '¼' },
];

/**
 * The shed: bins come in chalked, get checked, and leave with a printed tag.
 * Type the chalk code to find a bin (the list is today's untagged bins on the
 * farm, so a short code is enough), set how full it is, receive and print.
 * A bin that arrives with no record is made here — never refused.
 */
export default function ReceivePanel({ settings }: { settings: HarvestSettings | null }) {
  const tz = useOrgTimezone();
  const [date, setDate] = useState(getTodayInTz(tz));
  const [farms, setFarms] = useState<{ id: number; name: string }[]>([]);
  const [farmId, setFarmId] = useState<number | ''>('');
  const [q, setQ] = useState('');
  const [bins, setBins] = useState<Bin[]>([]);
  const [picked, setPicked] = useState<Map<number, number>>(new Map());
  const [size, setSize] = useState<LabelSize>(settings?.label_size ?? '100x150');
  const [printed, setPrinted] = useState<Bin[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [fixing, setFixing] = useState<Bin | null>(null);

  useEffect(() => { if (settings) setSize(settings.label_size); }, [settings]);
  useEffect(() => {
    orchardService.getAllFarms().then(r => {
      if (!r.success) return;
      const f = r.data.farms.map(x => ({ id: x.id, name: x.name }));
      setFarms(f);
      if (f.length === 1) setFarmId(f[0].id);
    }).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    try {
      setBins(await harvestService.shedLookup(date, farmId || undefined, q || undefined));
      setError(null);
    } catch (e) {
      setError(errorMessage(e, 'Could not load bins'));
    }
  }, [date, farmId, q]);
  useEffect(() => { load(); }, [load]);

  const toggle = (b: Bin) => setPicked(m => {
    const n = new Map(m);
    if (n.has(b.id)) n.delete(b.id); else n.set(b.id, b.fill_fraction);
    return n;
  });

  const receiveAndPrint = async () => {
    setBusy(true);
    setError(null);
    try {
      const done = await harvestService.receive([...picked].map(([id, fill]) => ({ id, fill_fraction: fill })));
      setPrinted(done);
      setPicked(new Map());
      await harvestService.downloadLabels(done.map(b => b.id), size);
      await load();
    } catch (e) {
      setError(errorMessage(e, 'Could not receive the bins'));
    } finally {
      setBusy(false);
    }
  };

  const reviewCount = useMemo(() => bins.filter(b => b.needs_review).length, [bins]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <label className="text-xs text-neutral-600">
          <span className="block mb-1">Picked on</span>
          <input type="date" className={inputClass} value={date} onChange={e => setDate(e.target.value)} />
        </label>
        {farms.length > 1 && (
          <label className="text-xs text-neutral-600">
            <span className="block mb-1">Farm</span>
            <select className={inputClass} value={farmId} onChange={e => setFarmId(e.target.value ? Number(e.target.value) : '')}>
              <option value="">All farms</option>
              {farms.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
            </select>
          </label>
        )}
        <label className="text-xs text-neutral-600 flex-1 min-w-[10rem]">
          <span className="block mb-1">Chalk code</span>
          <span className="relative block">
            <Search className="h-4 w-4 text-neutral-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input className={`${inputClass} pl-9 uppercase`} value={q} placeholder="e.g. 14-07"
              onChange={e => setQ(e.target.value.toUpperCase())} />
          </span>
        </label>
        <Button size="sm" variant="outline" onClick={() => setCreating(true)}><Plus className="h-4 w-4" /> Bin with no record</Button>
      </div>

      {error && <Notice tone="error">{error}</Notice>}
      {reviewCount > 0 && (
        <Notice tone="warn">{reviewCount} {reviewCount === 1 ? 'bin needs' : 'bins need'} the block confirmed before they are received.</Notice>
      )}

      {bins.length === 0 ? (
        <p className="text-sm text-neutral-500 py-6 text-center">No untagged bins {q ? 'match that code' : 'for this day'}.</p>
      ) : (
        <ul className="divide-y divide-neutral-100 border border-neutral-200 rounded-lg" aria-label="Bins waiting">
          {bins.map(b => (
            <li key={b.id} className="px-3 py-3 flex flex-wrap items-center gap-3">
              <input type="checkbox" aria-label={`Select bin ${b.field_code ?? b.id}`} checked={picked.has(b.id)}
                disabled={b.needs_review} onChange={() => toggle(b)}
                className="rounded border-neutral-300 text-indigo-600 focus:ring-indigo-200" />
              <span className="font-mono text-lg font-semibold text-neutral-900 w-20">{b.field_code ?? '—'}</span>
              <div className="flex-1 min-w-[10rem]">
                <p className="text-sm text-neutral-900">
                  {binPlace(b)}{b.row_number ? ` · row ${b.row_number}` : ''}
                </p>
                <p className="text-xs text-neutral-500">
                  {b.cultivar_name ?? ''}{b.pickers.length ? ` · ${b.pickers.map(p => workerDisplayName(p)).join(', ')}` : ''}
                </p>
                {b.duplicate_field_code && <p className="text-xs text-amber-700">Another bin today has this chalk code — check the pickers.</p>}
                {b.needs_review && (
                  <p className="text-xs text-amber-700 flex items-center gap-1">
                    <AlertTriangle className="h-3.5 w-3.5" /> {b.review_reason ?? 'Block to confirm'} ·{' '}
                    <button type="button" className="underline" onClick={() => setFixing(b)}>Set block</button>
                  </p>
                )}
              </div>
              {picked.has(b.id) && (
                <select aria-label="How full" value={picked.get(b.id)} onChange={e => setPicked(m => new Map(m).set(b.id, Number(e.target.value)))}
                  className="border border-neutral-300 rounded-lg px-2 py-1 text-sm bg-white">
                  {FILLS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                </select>
              )}
            </li>
          ))}
        </ul>
      )}

      <div className="flex flex-wrap items-end gap-3">
        <label className="text-xs text-neutral-600">
          <span className="block mb-1">Label size</span>
          <select className={inputClass} value={size} onChange={e => setSize(e.target.value as LabelSize)}>
            {(settings?.label_sizes ?? (Object.keys(LABEL_SIZE_LABELS) as LabelSize[])).map(s => <option key={s} value={s}>{LABEL_SIZE_LABELS[s]}</option>)}
          </select>
        </label>
        <Button variant="primary" disabled={!picked.size || busy} onClick={receiveAndPrint}>
          <Printer className="h-4 w-4" /> {busy ? 'Working…' : `Receive ${picked.size || ''} & print labels`.replace(/\s+/g, ' ')}
        </Button>
      </div>

      {printed.length > 0 && (
        <div className="rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-800 space-y-2">
          <p>Tagged: <span className="font-mono">{printed.map(b => b.tag_code).join(', ')}</span></p>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={() => harvestService.downloadLabels(printed.map(b => b.id), size)}>Print again</Button>
            <Button size="sm" variant="outline" onClick={() => harvestService.downloadLabels(printed.map(b => b.id), size, 'zpl')}>Zebra (ZPL)</Button>
          </div>
        </div>
      )}

      {creating && (
        <NoRecordModal date={date} onClose={() => setCreating(false)}
          onCreated={async (b) => { setCreating(false); setPrinted([b]); await harvestService.downloadLabels([b.id], size); load(); }} />
      )}
      {fixing && (
        <SetBlockModal bin={fixing} onClose={() => setFixing(null)} onSaved={() => { setFixing(null); load(); }} />
      )}
    </div>
  );
}

function AreaSelect({ value, onChange, id }: { value: number | ''; onChange: (v: number | '') => void; id: string }) {
  const places = usePlaceOptions().filter(p => p.target.type === 'cultivar_area');
  const groups = [...new Set(places.map(p => p.group))];
  return (
    <select id={id} className={inputClass} value={value} onChange={e => onChange(e.target.value ? Number(e.target.value) : '')}>
      <option value="">Choose…</option>
      {groups.map(g => (
        <optgroup key={g} label={g}>
          {places.filter(p => p.group === g).map(p => <option key={p.key} value={p.target.id}>{p.label}</option>)}
        </optgroup>
      ))}
    </select>
  );
}

function NoRecordModal({ date, onClose, onCreated }: { date: string; onClose: () => void; onCreated: (b: Bin) => void }) {
  const [area, setArea] = useState<number | ''>('');
  const [code, setCode] = useState('');
  const [fill, setFill] = useState(1);
  const [workers, setWorkers] = useState<Worker[]>([]);
  const [pickers, setPickers] = useState<number[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { fieldWorkService.getWorkers().then(setWorkers).catch(() => {}); }, []);

  const submit = async () => {
    setSaving(true);
    try {
      onCreated(await harvestService.createAtShed({
        cultivar_area_id: Number(area), worker_ids: pickers, field_code: code || null, fill_fraction: fill, date,
      }));
    } catch (e) {
      setError(errorMessage(e, 'Could not make the bin'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <FormModal title="Bin with no record" submitLabel="Make, tag & print" onClose={onClose} onSubmit={submit}
      submitting={saving} submitDisabled={!area} error={error}>
      <Field label="Picked in" htmlFor="nr-area"><AreaSelect id="nr-area" value={area} onChange={setArea} /></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Chalk code" htmlFor="nr-code">
          <input id="nr-code" className={`${inputClass} uppercase`} value={code} onChange={e => setCode(e.target.value.toUpperCase())} />
        </Field>
        <Field label="How full" htmlFor="nr-fill">
          <select id="nr-fill" className={inputClass} value={fill} onChange={e => setFill(Number(e.target.value))}>
            {FILLS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
          </select>
        </Field>
      </div>
      <Field label="Pickers" htmlFor="nr-pickers">
        <select id="nr-pickers" className={inputClass} value="" onChange={e => e.target.value && setPickers(p => [...new Set([...p, Number(e.target.value)])])}>
          <option value="">Add a picker…</option>
          {workers.filter(w => !pickers.includes(w.id)).map(w => <option key={w.id} value={w.id}>{workerDisplayName(w)} ({w.picker_code})</option>)}
        </select>
      </Field>
      {pickers.length > 0 && (
        <p className="text-sm text-neutral-700">{pickers.map(id => workers.find(w => w.id === id)).filter(Boolean).map(w => workerDisplayName(w as Worker)).join(', ')}</p>
      )}
    </FormModal>
  );
}

function SetBlockModal({ bin, onClose, onSaved }: { bin: Bin; onClose: () => void; onSaved: () => void }) {
  const [area, setArea] = useState<number | ''>('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async () => {
    setSaving(true);
    try {
      await harvestService.updateBin(bin.id, { cultivar_area_id: Number(area), reason: 'Block confirmed at the shed' });
      onSaved();
    } catch (e) {
      setError(errorMessage(e, 'Could not save'));
    } finally {
      setSaving(false);
    }
  };
  return (
    <FormModal title={`Where was ${bin.field_code ?? 'this bin'} picked?`} onClose={onClose} onSubmit={submit}
      submitting={saving} submitDisabled={!area} error={error}>
      <Field label="Cultivar area" htmlFor="sb-area"><AreaSelect id="sb-area" value={area} onChange={setArea} /></Field>
    </FormModal>
  );
}
